import * as palette from "@govlaunch/palette";
import upperFirst from "lodash/fp/upperFirst";
import numeral from "numeral";
import React, { Fragment } from "react";
import useIsMobile from "~/lib/hooks/useIsMobile";
import { ICompanyAccountBillingInfoQuery } from "~/lib/queries/__generated__/CompanyAccountBillingInfoQuery.generated";
import { IInvoiceStatus } from "~/types/types";
import getIntercomTarget from "~/components/vendor/profile/getIntercomTarget";
import { useSelfie } from "~/components/auth/Auth";
import { FormattedMessage } from "react-intl";
import IntlDateTime from "~/src/shared/components/IntlDateTime";

interface IPaymentHistoryProps {
  company: NonNullable<ICompanyAccountBillingInfoQuery["company"]>;
}

export default function PaymentHistory({ company }: IPaymentHistoryProps) {
  const isMobile = useIsMobile();
  const selfie = useSelfie();

  if (!selfie) {
    return null;
  }

  return (
    <div
      data-intercom-target={getIntercomTarget(selfie, {
        asMember: "Settings Payment History Box",
        asOwner: "Settings Payment History Box",
        asDisruptor: "Settings Payment History Box for Disruptors",
      })}
      css={{
        padding: 24,
        backgroundColor: palette.white,
        border: "solid 0.5px #e6e8ea",
        borderRadius: 4,
      }}
    >
      <h1
        css={{
          margin: 0,
          color: "#212121",
          fontWeight: 600,
          fontSize: 20,
        }}
      >
        <FormattedMessage defaultMessage="Invoices" id="6dqYnF" />
      </h1>

      <hr
        css={{
          width: "100%",
          marginTop: 20,
          border: "none",
          borderBottom: `solid 1px #e6e8ea`,
        }}
      />

      <table
        css={{
          boxSizing: "border-box",
          width: "100%",
          borderCollapse: "collapse",
          "> thead > tr > th": {
            boxSizing: "border-box",
            padding: 12,
            color: "#949195",
            fontWeight: 600,
            fontSize: 16,
            lineHeight: 1.33,
            letterSpacing: 0.6,
            textAlign: "left",
          },
          "& > tbody > tr > td": {
            boxSizing: "border-box",
            padding: 12,
            color: "#949195",
            fontSize: 14,
            lineHeight: 1.33,
            letterSpacing: 0.6,
            textAlign: "left",
          },
        }}
      >
        <thead>
          <tr>
            {isMobile ? null : <th>ID</th>}
            <th>
              <FormattedMessage defaultMessage="Date" id="P7PLVj" />
            </th>
            <th>
              <FormattedMessage defaultMessage="Card" id="hmnIY5" />
            </th>
            <th>
              <FormattedMessage defaultMessage="Amount" id="/0TOL5" />
            </th>
            <th>
              <FormattedMessage defaultMessage="Status" id="tzMNF3" />
            </th>
            <th>
              <FormattedMessage defaultMessage="Receipt" id="D0tozv" />
            </th>
          </tr>
        </thead>

        <tbody>
          {company.invoices.length === 0 && (
            <tr>
              <td colSpan={isMobile ? 5 : 6}>
                <div
                  css={{
                    textAlign: "center",
                  }}
                >
                  <FormattedMessage defaultMessage="You don't have any invoices/charges." id="Yji5D+" />
                </div>
              </td>
            </tr>
          )}

          {company.invoices.map((invoice) => {
            const isPartiallyRefunded =
              invoice.amountRefunded > 0 && invoice.amountRefunded !== invoice.amount && invoice.amount !== 0;
            const isCompletelyRefunded = invoice.amountRefunded === invoice.amount && invoice.amount !== 0;
            const isRefunded = isPartiallyRefunded || isCompletelyRefunded;

            const invoiceItems = invoice.items.map((invoiceItem) => {
              return (
                <tr key={invoiceItem.id}>
                  <td colSpan={1}>&nbsp;</td>
                  <td colSpan={3}>{invoiceItem.description || "Invoice Item"}</td>
                  <td colSpan={2}>&nbsp;</td>
                </tr>
              );
            });

            return (
              <Fragment key={invoice.friendlyId}>
                <tr>
                  {isMobile ? null : <td>{invoice.friendlyId}</td>}

                  <td>{invoice.paidAt ? <IntlDateTime value={invoice.paidAt} /> : "N/A"}</td>
                  <td>
                    {invoice.card ? (
                      <FormattedMessage
                        defaultMessage="{cardBrand} **** {lastFourDigits}"
                        id="sPwgku"
                        values={{
                          cardBrand: upperFirst(invoice.card.brand),
                          lastFourDigits: invoice.card.lastFourDigits,
                        }}
                      />
                    ) : (
                      "N/A"
                    )}
                  </td>
                  <td
                    css={{
                      fontWeight: 500,
                      color: palette.darkGray,
                    }}
                  >
                    {isPartiallyRefunded ? (
                      <span>
                        {invoice.currency === "USD" ? "$" : null}
                        {numeral(invoice.amountRefunded / 100).format("0,00.00")}
                        of
                        {invoice.currency === "USD" ? "$" : null} {numeral(invoice.amount / 100).format("0,00.00")}
                      </span>
                    ) : (
                      <span
                        css={{
                          textDecoration: isCompletelyRefunded ? "line-through" : undefined,
                        }}
                      >
                        {invoice.currency === "USD" ? "$" : null}
                        {numeral(invoice.amount / 100).format("0,00.00")}
                      </span>
                    )}
                  </td>

                  <td>
                    {invoice.status === IInvoiceStatus.Paid && isRefunded && (
                      <span
                        css={{
                          color: palette.text,
                          fontWeight: "bold",
                        }}
                      >
                        <FormattedMessage defaultMessage="Refunded" id="Gs86nL" />
                      </span>
                    )}

                    {invoice.status === IInvoiceStatus.Paid && !isRefunded && (
                      <span>
                        <FormattedMessage defaultMessage="Paid" id="u/vOPu" />
                      </span>
                    )}

                    {invoice.status === IInvoiceStatus.Void && (
                      <span
                        css={{
                          color: palette.red,
                          fontWeight: "bold",
                        }}
                      >
                        <FormattedMessage defaultMessage="Voided" id="DqBuv6" />
                      </span>
                    )}

                    {invoice.status === IInvoiceStatus.Uncollectible && (
                      <span
                        css={{
                          color: palette.red,
                          fontWeight: "bold",
                        }}
                      >
                        <FormattedMessage defaultMessage="Uncollectible" id="45hFQt" />
                      </span>
                    )}

                    {invoice.status === IInvoiceStatus.Open && (
                      <span>
                        <FormattedMessage defaultMessage="Open" id="JfG49w" />
                      </span>
                    )}
                  </td>

                  <td>
                    <a
                      href={invoice.receiptUrl || ""}
                      rel="noopener noreferrer"
                      target="_blank"
                      title={`Receipt for Charge ${invoice.id}`}
                      css={{
                        color: palette.darkSkyBlue,
                        fontWeight: 600,
                        textDecoration: "none",
                      }}
                    >
                      {invoice.status === IInvoiceStatus.Open ? (
                        <FormattedMessage defaultMessage="Pay" id="lD3+8a" />
                      ) : (
                        <FormattedMessage defaultMessage="View" id="FgydNe" />
                      )}
                    </a>
                  </td>
                </tr>
                {invoiceItems}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
